import {
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridRowLookup,
  GridTable,
  multiFilter,
  numericColumn,
  RowStyles,
  simpleHeader,
  Switch,
  Tooltip,
  useGridTableApi,
  useSuperDrawer,
} from "@homebound/beam";
import { pascalCase } from "change-case";
import { MutableRefObject, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { createBudgetUrl, createProjectExpensesUrl } from "src/RouteUrls";
import { Icon, priceCell, SearchBox } from "src/components";
import { LotType, ProjectItemFilter } from "src/generated/graphqlTypes";
import { useToggle } from "src/hooks";
import {
  HierarchyBudgetPage__ProjectSharedCostsFragment,
  HierarchyBudgetPage_CostCodeFragment,
  HierarchyBudgetPage_ProjectBudgetFinancialsFragment,
  HierarchyBudgetPage_ProjectFragment,
  HierarchyBudgetPage_ProjectItemAllocationFragment,
  HierarchyBudgetPage_ProjectItemFragment,
  HierarchyBudgetPage_UnallocatedExpenseFragment,
  useHierarchyBudgetPage_RollupViewQuery,
  useHierarchyBudgetPage_SharedCostsQuery,
  useHierarchyBudgetPageQuery,
} from "src/routes/projects/budget/HierarchyBudgetPage.generated";
import { ProjectParams } from "src/routes/routesDef";
import { formatCentsToPrice, queryResult } from "src/utils";
import { ProjectHeader } from "../ProjectHeader";
import { CostData, sumCostData } from "./budgetCalc";
import { BudgetSuperDrawer } from "./components/BudgetSuperDrawer";
import { useStorageFilter } from "./components/useStorageFilter";

export function HierarchyBudgetPage() {
  const { projectId } = useParams<ProjectParams>();
  const query = useHierarchyBudgetPageQuery({ variables: { projectId } });
  return queryResult(query, (data) => <HierarchyBudgetView project={data.project} costCodes={data.costCodes} />);
}

type HierarchyBudgetViewProps = {
  project: HierarchyBudgetPage_ProjectFragment;
  costCodes: HierarchyBudgetPage_CostCodeFragment[];
};

export function HierarchyBudgetView({ project, costCodes }: HierarchyBudgetViewProps) {
  const { id, name, isSellableProject, childrenRecursive } = project;
  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const [showRollupView, toggleRollupView] = useToggle(false);
  const [showSharedCosts, toggleSharedCosts] = useToggle(false);
  const { openInDrawer } = useSuperDrawer();
  const filterDefs = useMemo(() => createFilterDefs(costCodes), [costCodes]);
  const { setFilter, filter } = useStorageFilter<ProjectItemFilter>({ storageKey: "budgetPageFilter", filterDefs });
  // Query recursive children information for rollup view
  const { data: rollupViewData } = useHierarchyBudgetPage_RollupViewQuery({
    skip: !showRollupView,
    variables: { projectIds: [id, ...childrenRecursive.map((p) => p.id)], costCodes: filter?.costCode },
  });
  // Query project item allocations information for shared costs view
  const { data: sharedCostsData } = useHierarchyBudgetPage_SharedCostsQuery({
    skip: !showSharedCosts,
    variables: { projectId: id },
  });
  const columns = useMemo(() => createColumns(project), [project]);
  const rows = useMemo(
    () => createRows(project, filter, rollupViewData?.projects, sharedCostsData?.project),
    [project, filter, rollupViewData, sharedCostsData],
  );
  const rowLookup = useRef<GridRowLookup<Row>>();
  const rowStyles = useMemo(() => createRowStyles(openInDrawer, rowLookup, project.id), [openInDrawer, project.id]);
  const api = useGridTableApi<Row>();

  return (
    <div>
      <ProjectHeader projectId={id} title={`${name} Budget`} createUrl={createBudgetUrl} xss={Css.pb3.$} />
      <div css={Css.df.fdr.cg1.$}>
        <SearchBox onSearch={setSearchFilter} />
        <Filters<ProjectItemFilter> filter={filter} filterDefs={filterDefs} onChange={setFilter} />
        <div css={Css.mla.$}>
          <Button
            label="Download"
            variant="tertiary"
            onClick={() => api.downloadToCsv(`${name.replaceAll(" ", "_")}_budget.csv`)}
          />
        </div>
      </div>
      <div css={Css.py2.$}>
        {!isSellableProject ? (
          <Switch label="Rollup View" compact selected={showRollupView} onChange={toggleRollupView} />
        ) : (
          <Switch label="Show Shared Costs" compact selected={showSharedCosts} onChange={toggleSharedCosts} />
        )}
      </div>
      <GridTable
        api={api}
        columns={columns}
        rows={rows}
        filter={searchFilter}
        stickyHeader
        rowLookup={rowLookup}
        rowStyles={rowStyles}
      />
    </div>
  );
}

type HeaderRow = { kind: "header" };
type TotalsRow = { kind: "totals"; data: { costData: CostData } };
type CostTypeRow = { kind: "costType"; data: { costType: string; costData: CostData }; id: string };
type UnallocatedExpensesHeaderRow = {
  id: string;
  kind: "unallocatedExpensesHeader";
  data: { project: HierarchyBudgetPage_ProjectFragment; costData: CostData };
};
type UnallocatedExpensesRow = {
  id: string;
  kind: "unallocatedExpense";
  data: { expense: HierarchyBudgetPage_UnallocatedExpenseFragment; costData: CostData };
};
type CostDivisionRow = { kind: "costDivision"; data: { costDivision: string; costData: CostData }; id: string };
type CostCodeRow = {
  kind: "costCode";
  id: string;
  data: { costCode: HierarchyBudgetPage_CostCodeFragment; costData: CostData };
};
type ProjectRow = {
  kind: "project";
  id: string;
  data: { project: HierarchyBudgetPage_ProjectBudgetFinancialsFragment; costData: CostData };
};
type Row =
  | HeaderRow
  | TotalsRow
  | CostTypeRow
  | CostDivisionRow
  | CostCodeRow
  | ProjectRow
  | UnallocatedExpensesHeaderRow
  | UnallocatedExpensesRow;

function createColumns({ lotType, isSellableProject }: HierarchyBudgetPage_ProjectFragment): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ totals: emptyCell }),
    column<Row>({
      header: "Cost Code",
      totals: () => ({ content: <div css={Css.gray900.xsSb.$}>Totals</div>, value: "Totals" }),
      costType: ({ costType }) => ({ content: <div css={Css.xsSb.$}>{costType}</div>, value: costType }),
      costDivision: ({ costDivision }) => ({
        content: <div css={Css.xs.mlPx(-12).$}>{costDivision}</div>,
        value: costDivision,
      }),
      costCode: ({ costCode }) => costCode.displayName,
      project: ({ project }) => ({
        content: (
          <Link to={createBudgetUrl(project.id)} target="_blank" className="navLink">
            <div css={Css.blue600.xsSb.if(project.children.isEmpty).xs.$}>{project.name}</div>
          </Link>
        ),
        value: project.name,
      }),
      unallocatedExpensesHeader: (data) => ({
        content: (
          <Link to={createProjectExpensesUrl(data.project.id)} target="_blank">
            <div css={Css.blue600.xsSb.$}>Unallocated Expenses</div>
          </Link>
        ),
        value: "Unallocated Expenses",
      }),
      unallocatedExpense: ({ expense }) => ({
        content: (
          <div>
            <span css={Css.dib.if(!expense.costCode).red600.$}>
              {expense.costCode ? expense.costCode.displayName : `No Code `}
              {` - ${expense.intacctVendorName} - ${expense.description}`}
            </span>
          </div>
        ),
        value: `${expense.costCode ? expense.costCode.displayName : `No Code `} - ${expense.intacctVendorName} - ${expense.description}`,
      }),
      mw: "240px",
    }),
    createCostDataColumn(
      "originalCostInCents",
      lotType.code === LotType.Bool ? "Underwritten" : "Original",
      lotType.code === LotType.Bool ? "Total underwritten budget" : "Total cost from the original contract",
    ),
    ...(isSellableProject
      ? [
          createCostDataColumn(
            "startCostInCents",
            "Start",
            "A snapshot of the budget for this project at the RTS milestone.",
          ),
        ]
      : []),
    createCostDataColumn("changeEventCostInCents", "Change Events", "All change event costs"),
    createCostDataColumn("revisedCostInCents", "Revised", "Underwritten budget + all accepted Change Events"),
    createCostDataColumn(
      "projectedCostInCents",
      "Projected",
      "Revised Budget + proposed Change Events + requested Change Events",
    ),
    createCostDataColumn("committedCostInCents", "Committed", "Sum of all signed POs and expenses"),
    createCostDataColumn("availableCostInCents", "Available", "Revised - committed - approved overhead bills"),
    createCostDataColumn("billedCostInCents", "Trade Billed", "Approved bills for trade partners"),
    createCostDataColumn(
      "unbilledCostInCents",
      "Unbilled",
      "Unbilled amount from existing commitments. Includes unapproved bills.",
    ),
  ];
}

function createCostDataColumn(key: keyof CostData, header: string, tooltipText: string) {
  return numericColumn<Row>({
    header: () => ({
      content: (
        <div css={Css.gray700.xs.df.cg1.$}>
          {header}
          <Tooltip title={tooltipText} placement="top">
            <Icon icon="infoCircle" inc={2} xss={Css.mr1.$} />
          </Tooltip>
        </div>
      ),
      value: header,
    }),
    totals: ({ costData }) => ({
      content: <div css={Css.gray900.xsSb.$}> {formatCentsToPrice(costData[key] ?? 0)} </div>,
      value: formatCentsToPrice(costData[key] ?? 0),
    }),
    costType: ({ costData }) => ({
      content: <div css={Css.gray900.xsSb.$}> {formatCentsToPrice(costData[key] ?? 0)} </div>,
      value: formatCentsToPrice(costData[key] ?? 0),
    }),
    costDivision: ({ costData }) => priceCell({ valueInCents: costData[key] }),
    costCode: ({ costData }) => priceCell({ valueInCents: costData[key] }),
    project: ({ project, costData }) =>
      project.children.isEmpty
        ? priceCell({ valueInCents: costData[key] })
        : {
            content: <div css={Css.gray900.xsSb.$}> {formatCentsToPrice(costData[key] ?? 0)} </div>,
            value: formatCentsToPrice(costData[key] ?? 0),
          },
    unallocatedExpensesHeader: ({ costData }) => priceCell({ valueInCents: costData[key] }),
    unallocatedExpense: ({ costData }) => priceCell({ valueInCents: costData[key] }),
    mw: header.split(" ").length > 1 ? "150px" : undefined, // Adjust width for multi-word headers
  });
}

function createRows(
  project: HierarchyBudgetPage_ProjectFragment,
  filter: ProjectItemFilter,
  rollupViewData: HierarchyBudgetPage_ProjectBudgetFinancialsFragment[] | undefined,
  sharedCostsData: HierarchyBudgetPage__ProjectSharedCostsFragment | undefined,
): GridDataRow<Row>[] {
  if (rollupViewData) {
    return [
      simpleHeader,
      { kind: "totals" as const, id: "totals", data: { costData: rollupViewData.reduce(sumCostData, {}) } },
      ...groupByHierarchyLevel(project, rollupViewData),
    ];
  }

  const filteredProjectItems = project.stages
    .flatMap((s) => s.projectItems)
    .filter((pi) => (!filter?.costCode || filter.costCode.includes(pi.item.costCode?.id) ? pi : undefined));

  const filteredProjectItemAllocations = sharedCostsData?.projectItemAllocations.filter((pi) =>
    !filter?.costCode || filter.costCode.includes(pi.parentProjectItem.item.costCode?.id) ? pi : undefined,
  );

  const filteredUnallocatedExpenses = project.unallocatedExpenses.filter(
    (ue) => !filter?.costCode || (ue.costCode && filter.costCode.includes(ue.costCode.id)),
  );

  const totalUnallocatedExpenseAmountInCents = filteredUnallocatedExpenses.sum((ue) => ue.unallocatedAmountInCents);
  const unallocatedExpensesHeaderRows: GridDataRow<Row> = {
    kind: "unallocatedExpensesHeader" as const,
    id: "unallocatedExpensesHeader",
    initCollapsed: true,
    data: {
      project,
      costData: {
        committedCostInCents: totalUnallocatedExpenseAmountInCents,
        billedCostInCents: totalUnallocatedExpenseAmountInCents,
      },
    },
    children: filteredUnallocatedExpenses.map((expense) => ({
      kind: "unallocatedExpense" as const,
      id: expense.id,
      data: {
        expense,
        costData: {
          committedCostInCents: expense.unallocatedAmountInCents,
          billedCostInCents: expense.unallocatedAmountInCents,
        },
      },
    })),
  };

  return [
    simpleHeader,
    {
      kind: "totals" as const,
      id: "totals",
      data: {
        costData: [
          ...filteredProjectItems,
          ...(filteredProjectItemAllocations ?? []),
          unallocatedExpensesHeaderRows.data.costData,
        ].reduce(sumCostData, {}),
      },
    },
    ...(filteredUnallocatedExpenses.nonEmpty ? [unallocatedExpensesHeaderRows] : []),
    ...groupByCostType(filteredProjectItems, filteredProjectItemAllocations),
  ];
}

function groupByCostType(
  projectItems: HierarchyBudgetPage_ProjectItemFragment[],
  projectItemAllocations: HierarchyBudgetPage_ProjectItemAllocationFragment[] | undefined,
): GridDataRow<Row>[] {
  // Group by cost classification type -> cost division -> cost code
  const rows = projectItems
    .groupBy((pi) => pi.item.costCode.costClassification.code)
    .toEntries()
    .map(([costType, pisByCostType]) => ({
      kind: "costType" as const,
      id: costType,
      data: { costType: `${pascalCase(costType)} Costs`, costData: pisByCostType.reduce(sumCostData, {}) },
      children: pisByCostType
        .groupBy((pi) => pi.item.costCode.division.id)
        .toEntries()
        .map(([costDivisionId, pisByCostDivision]) => ({
          kind: "costDivision" as const,
          id: `${costType}${costDivisionId}`,
          data: {
            costDivision: pisByCostDivision.first!.item.costCode.division.displayName,
            costData: pisByCostDivision.reduce(sumCostData, {}),
          },
          children: pisByCostDivision
            .groupBy((pi) => pi.item.costCode.id)
            .toEntries()
            .map(([costCodeId, pisByCostCode]) => ({
              kind: "costCode" as const,
              id: costCodeId,
              data: { costCode: pisByCostCode.first!.item.costCode, costData: pisByCostCode.reduce(sumCostData, {}) },
            })),
        })),
    }));
  // Add shared costs rows if applicable
  const sharedCostsRows = projectItemAllocations
    ? [
        {
          kind: "costType" as const,
          id: "sharedCosts",
          data: { costType: "Shared Costs", costData: projectItemAllocations.reduce(sumCostData, {}) },
          children: projectItemAllocations
            .groupBy((pia) => pia.parentProjectItem.item.costCode.division.id)
            .toEntries()
            .map(([costDivisionId, piasByCostDivision]) => ({
              kind: "costDivision" as const,
              id: `shared-${costDivisionId}`,
              data: {
                costDivision: piasByCostDivision.first!.parentProjectItem.item.costCode.division.displayName,
                costData: piasByCostDivision.reduce(sumCostData, {}),
              },
              children: piasByCostDivision
                .groupBy((pia) => pia.parentProjectItem.item.costCode.id)
                .toEntries()
                .map(([costCodeId, piasByCostCode]) => ({
                  kind: "costCode" as const,
                  id: `shared-${costCodeId}`,
                  data: {
                    costCode: piasByCostCode.first!.parentProjectItem.item.costCode,
                    costData: piasByCostCode.reduce(sumCostData, {}),
                  },
                })),
            })),
        },
      ]
    : [];

  return [...sharedCostsRows, ...rows];
}

function groupByHierarchyLevel(
  forProject: HierarchyBudgetPage_ProjectFragment,
  projects: HierarchyBudgetPage_ProjectBudgetFinancialsFragment[],
): GridDataRow<Row>[] {
  const rootProject = projects.find((p) => p.id === forProject.id);
  // Build a tree of project rows
  function buildRow(project: HierarchyBudgetPage_ProjectBudgetFinancialsFragment): GridDataRow<Row> {
    const childProjects = project.children.map((child) => projects.find((p) => p.id === child.id)).compact();
    const childrenRecursive = project.childrenRecursive
      .map((child) => projects.find((p) => p.id === child.id))
      .compact();
    return {
      kind: "project",
      id: project.id,
      data: { project, costData: [project, ...childrenRecursive].reduce(sumCostData, {}) }, // Include project + nested children values
      children: childProjects.map(buildRow),
      initCollapsed: project !== rootProject,
    };
  }
  return rootProject ? [buildRow(rootProject)] : [];
}

function createFilterDefs(costCodes: HierarchyBudgetPage_CostCodeFragment[]): FilterDefs<ProjectItemFilter> {
  return {
    costCode: multiFilter({
      options: costCodes,
      label: "Cost Code",
      getOptionValue: (costCode) => costCode.id,
      getOptionLabel: (costCode) => costCode.displayName,
    }),
  };
}

function createRowStyles(
  openInDrawer: ReturnType<typeof useSuperDrawer>["openInDrawer"],
  lookup: MutableRefObject<GridRowLookup<Row> | undefined>,
  projectId: string,
): RowStyles<Row> {
  function openRow(row: GridDataRow<Row>): void {
    // Filters out shared cost codes since they are not clickable
    if (row.kind === "costCode" && !row.id.startsWith("shared-")) {
      const { prev, next } = lookup.current!.lookup(row, (r) => r.kind === "costCode");

      openInDrawer({
        onPrevClick: prev && (() => openRow(prev)),
        onNextClick: next && (() => openRow(next)),
        content: (
          <BudgetSuperDrawer
            costCodeIds={[row.data.costCode.id]}
            title={row.data.costCode.displayName}
            projectId={projectId}
            isPiRow={false}
          />
        ),
      });
    }
  }
  return { costCode: { onClick: openRow } };
}
